import { Component, AfterViewInit, ViewChild, ElementRef, Inject, OnInit, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import SignaturePad from 'signature_pad';
import { Location } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { DriverQualificationService } from '../service/driver-qualification.service';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { ROUTE_DQF, ROUTE_DQF_DOCUMENT_UPLOAD } from 'src/app/shared/routes';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { DQF_DOC_STATUS, DRIVER_DOC_CONTEXT_INITIAL_DQF } from '../constants';
import { SubSink } from 'subsink';
import { first, catchError } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';

const ISO_8601 = 'YYYY-MM-DDTHH:mm:ss[Z]';
const DISABLE_TOP_LOADING_BAR = true;
const UPLOADED_DOCUMENT = 'Successfully uploaded document';
@Component({
  selector: 'app-dqf-driver-signature',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatProgressSpinnerModule, FormsModule],
  templateUrl: './dqf-driver-signature.component.html',
  styleUrls: ['./dqf-driver-signature.component.scss']
})
export class DqfDriverSignatureComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('signatureCanvas') signatureCanvas!: ElementRef<HTMLCanvasElement>;
  signaturePad!: SignaturePad;
  dqfData: any;
  driverDetails: any;
  uploadUrl: any;
  file: any;
  subSink = new SubSink();
  place: string = '';
  formSubmitted: boolean = false;
  hasSignature: boolean = false;
  isLoading: boolean = false;

  constructor(
    private readonly router: Router,
    private readonly location: Location,
    private readonly driverQualificationService: DriverQualificationService,
    private readonly toastService: ToastService,
    private readonly ngZone: NgZone,
    private dateTimeService: DateTimeService
  ) {}

  ngOnInit(): void {
    const state = history.state;
    if (state?.dqfData && state?.driverDetails) {
      this.dqfData = state.dqfData;
      this.driverDetails = state.driverDetails;
      this.uploadUrl = state.uploadUrl;
    } else {
      console.warn('No navigation state found');
      this.backAction();
    }
  }
  ngAfterViewInit(): void {
    const canvas = this.signatureCanvas.nativeElement;
    
    // Initialize canvas with proper dimensions before creating SignaturePad
    const container = canvas.parentElement;
    if (container) {
      const rect = container.getBoundingClientRect();
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      
      const context = canvas.getContext('2d');
      if (context) {
        context.scale(ratio, ratio);
      }
    }
    
    this.signaturePad = new SignaturePad(this.signatureCanvas.nativeElement, {
      minWidth: 1,
      maxWidth: 3,
      penColor: 'black',
      backgroundColor: 'white',
      throttle: 16,
      minDistance: 5
    });

    this.signaturePad.addEventListener('beginStroke', () => {
      this.ngZone.run(() => {
        // Don't set hasSignature to true on begin, wait for end to validate
      });
    });
    
    this.signaturePad.addEventListener('endStroke', () => {
      this.ngZone.run(() => {
        this.hasSignature = this.hasValidSignature();
      });
    });
    
    window.addEventListener('resize', () => this.resizeCanvas());
  }

  private resizeCanvas(): void {
    const canvas = this.signatureCanvas.nativeElement;
    const container = canvas.parentElement;
    
    if (container) {
      // Save the signature data before resizing
      const signatureData = this.signaturePad.isEmpty() ? null : this.signaturePad.toData();
      
      const rect = container.getBoundingClientRect();
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      
      // Set canvas size accounting for device pixel ratio
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      
      // Scale the context to account for device pixel ratio
      const context = canvas.getContext('2d');
      if (context) {
        context.scale(ratio, ratio);
      }
      
      // Restore the signature data after resizing
      if (signatureData) {
        this.signaturePad.fromData(signatureData);
      }
      
      // Re-apply signature pad settings after resize
      this.signaturePad.minWidth = 1;
      this.signaturePad.maxWidth = 3;
      this.signaturePad.penColor = 'black';
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', () => this.resizeCanvas());
  }

  backAction(): void {
    this.location.back();
  }
  clearSignature(): void {
    this.signaturePad.clear();
    this.hasSignature = false;
  }

  private hasValidSignature(): boolean {
    if (this.signaturePad.isEmpty()) {
      return false;
    }
    const data = this.signaturePad.toData();
    if (data.length === 0) {
      return false;
    }
    const totalPoints = data.reduce((total, stroke) => total + stroke.points.length, 0);
    return totalPoints > 5; 
  }

  async saveSignature(): Promise<void> {
    this.formSubmitted = true;
    
    if (this.signaturePad.isEmpty()) {
      this.toastService.showError('Please provide your signature.');
      return;
    }

    if (!this.place || this.place.trim() === '') {
      this.toastService.showError('Please enter the place.');
      return;
    }

    if (!this.uploadUrl) {
      this.toastService.showError('Upload URL is not available.');
      return;
    }

    this.isLoading = true;

    await this.generatePDF();

    if (!this.file) {
      this.toastService.showError('Failed to generate PDF.');
      this.isLoading = false;
      return;
    }

    const generationDate = new Date().toUTCString();
    const SOURCE_TEXT = '-';
    const extension = 'pdf';
    const reportType = 'EmploymentHistoryCheck';
    const signatureReportType = 'EmploymentHistoryCheckSignature';

    const docModel: any = {
      source: SOURCE_TEXT,
      extension: extension,
      generationDate: this.dateTimeService.formatDateTime(generationDate, ISO_8601),
      reportType: reportType,
      context: DRIVER_DOC_CONTEXT_INITIAL_DQF,
      dqfId: this.driverQualificationService.dqfId
    };

    const signatureModel: any = {
      source: 'self',
      extension: 'png',
      generationDate: this.dateTimeService.formatDateTime(generationDate, ISO_8601),
      reportType: signatureReportType,
      context: DRIVER_DOC_CONTEXT_INITIAL_DQF,
      dqfId: this.driverQualificationService.dqfId
    };

    const signatureBlob: Blob | null = await new Promise(resolve =>
      this.signatureCanvas.nativeElement.toBlob(resolve, 'image/png')
    );

    if (!signatureBlob) {
      this.toastService.showError('Failed to generate signature image.');
      return;
    }

    forkJoin([
      this.driverQualificationService.uploadDqfDocument(docModel, this.file, DISABLE_TOP_LOADING_BAR).pipe(
        first(),
        catchError(error => {
          console.error('Document upload error:', error);
          this.isLoading = false;
          return of(false);
        })
      ),
      this.driverQualificationService.uploadDqfDocument(signatureModel, signatureBlob, DISABLE_TOP_LOADING_BAR).pipe(
        first(),
        catchError(error => {
          console.error('Signature upload error:', error);
          this.isLoading = false;
          return of(false);
        })
      )
    ]).subscribe(([docRes, sigRes]) => {
      const updateModel = {
        attachedDocument: {
          type: reportType,
          status: DQF_DOC_STATUS.DRIVER_REVIEWED
        }
      };
      this.updateDqfDocStatus(updateModel, UPLOADED_DOCUMENT);

      setTimeout(() => {
        this.ngZone.run(() => {
          this.isLoading = false;
          this.navigateBackToDqfDocuments();
        });
      }, 500);
    });
  }

  updateDqfDocStatus(docModel: any, successMessage: string): void {
    this.subSink.sink = this.driverQualificationService
      .updateDqfStatus(this.driverQualificationService.dqfId, docModel)
      .subscribe({
        next: () => {
          this.toastService.showSuccess(successMessage);
          setTimeout(() => {
            this.ngZone.run(() => {
              this.isLoading = false;
              this.navigateBackToDqfDocuments();
            });
          }, 500);
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  private navigateBackToDqfDocuments(): void {
    const dqfId = this.driverQualificationService.dqfId;
    if (dqfId) {
      this.router.navigate([ROUTE_DQF, ROUTE_DQF_DOCUMENT_UPLOAD, dqfId]);
    } else {
      this.router.navigate([ROUTE_DQF]);
    }
  }

  async generatePDF() {
    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([595.28, 841.89]);
    const { width, height } = page.getSize();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const borderMargin = 20;
    page.drawRectangle({
      x: borderMargin,
      y: borderMargin,
      width: width - (2 * borderMargin),
      height: height - (2 * borderMargin),
      borderColor: rgb(0.2, 0.2, 0.2),
      borderWidth: 1,
    });

    page.drawText('Employment History Check Consent Form', {
      x: 50,
      y: height - 50,
      size: 20,
      font: fontBold,
      color: rgb(0.1, 0.1, 0.1)
    });

    page.drawLine({
      start: { x: 50, y: height - 70 },
      end: { x: width - 50, y: height - 70 },
      thickness: 1,
      color: rgb(0.3, 0.3, 0.3)
    });

    const consentText = `I ${this.driverDetails.firstName} ${this.driverDetails.lastName} hereby authorize you to release the following information to ${this.dqfData.carrierCompanyName} for the purposes of investigation as required by Section 391.23 and allowed by Section 383.35 of the Federal Motor Carrier Safety Regulations. You are released from all liability which may result from furnishing such information.`;

    page.drawText(consentText, {
      x: 50,
      y: height - 100,
      size: 12,
      font,
      color: rgb(0.1, 0.1, 0.1),
      maxWidth: width - 100,
      lineHeight: 18
    });

    const subnote = `I understand that my signature below grants ${this.dqfData.carrierCompanyName} permission to request and receive this information and releases the previous employer from any liability resulting from the disclosure of such information.`;

    page.drawText(subnote, {
      x: 50,
      y: height - 210,
      size: 12,
      font,
      color: rgb(0.1, 0.1, 0.1),
      maxWidth: width - 100,
      lineHeight: 18
    });

    if (this.signatureCanvas?.nativeElement) {
      const canvas = this.signatureCanvas.nativeElement;
      const signatureDataUrl = canvas.toDataURL('image/png');
      const pngImageBytes = this.dataURLtoBytes(signatureDataUrl);
      const pngImage = await pdfDoc.embedPng(pngImageBytes);

      const pngDims = pngImage.scale(0.5);

      page.drawImage(pngImage, {
        x: 50,
        y: height - 400,
        width: pngDims.width,
        height: pngDims.height
      });
    }

    const driverName = `${this.driverDetails.firstName} ${this.driverDetails.lastName}`;
    page.drawText(driverName, {
      x: 50,
      y: height - 450,
      size: 14,
      font: fontBold,
      color: rgb(0, 0, 0)
    });

    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    page.drawText(this.place, {
      x: 50,
      y: height - 480,
      size: 12,
      font: fontBold,
      color: rgb(0.2, 0.2, 0.2)
    });

    page.drawText(currentDate, {
      x: 50,
      y: height - 500,
      size: 12,
      font: fontBold,
      color: rgb(0.2, 0.2, 0.2)
    });

    const pdfBytes = await pdfDoc.save();
    const pdfFile = new Blob([pdfBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
    this.file = pdfFile;
  }
  
  private dataURLtoBytes(dataURL: string): Uint8Array {
    const base64 = dataURL.split(',')[1];
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }
}
